/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import { useState } from "react";
import { Select, SelectOption, SelectOptionSearch } from "@forge/common";
import "./dropdown.scss";

interface IProps {
  defaultValue?: any;

  style?: any;
  onClick: () => void;
  className?: string;
  name?: string;
  items: any;
  placeholder?: string;
  placeholderSearch?: string;
  variant?: "defaultSelect" | "multiSelect" | "multiSelectIntractive";
  selectedLabel?: string;
  selectLabel?: [string, string];
  onSelect: (event: any) => void;
  useCheckIcon: boolean;
  key?: any;
  showIcon?: boolean;
  icon?: JSX.Element;
  enableSearch?: boolean;
  size?: "large" | "regular" | "small";
  onSearchChange?: (input: string) => void;
  isLoader?: boolean;
  isDisabled?: boolean;
  optionsLength?: 5 | 10;
  linkText?: string;
  onLinkClicked?: () => void;
  error?: boolean;
}

export const Dropdown = (props: IProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState(props.items);
  const [selectedValue, setSelectedValue] = useState(props.defaultValue ?? "");

  /**
   * Handles the search input change event in a dropdown component.
   *
   * This function is triggered whenever the search input within the dropdown changes. It performs several key actions:
   * 1. Prevents the event from propagating to parent elements to avoid triggering their event handlers.
   * 2. Updates the `searchTerm` state with the new value of the search input.
   * 3. Filters the `props.items` based on the search input value. It compares the lowercase version of each item's label
   *    with the lowercase version of the search input value to ensure case-insensitive matching.
   * 4. Updates the `options` state with the filtered list of items, which will be used to render the dropdown options.
   *
   * @param {any} e - The event object associated with the change event of the search input.
   */
  const onSearchChange = (e: any) => {
    e.stopPropagation(); // Prevent event from propagating to parent
    setSearchTerm(e.target.value);
    const filteredOptions = props.items.filter((item: any) =>
      item.label.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setOptions(filteredOptions);
  };

  return (
    // CID 3304393
    <Select
      style={{ width: "auto", whiteSpace: "nowrap" }}
      key={props.key ?? "defaultKey"}
      className={props.className ?? ""}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
        props.onSelect(event);
      }}
      error={props.error}
      placeholder={props.placeholder ?? "defaultPlaceholder"}
      search={
        <SelectOptionSearch
          onChange={onSearchChange}
          placeholder={props.placeholderSearch ?? "defaultSearchPlaceholder"} // Provide a default search placeholder
          value={searchTerm}
          label={""}
          className="dropdown-search"
        />
      }
      name={props.name}
      disabled={props.isDisabled}
      id="dropdown" // Add the 'id' property
      size={props.size}
      data-testid="dropdown-button"
      value={selectedValue}
    >
      {options?.map((item: any, index: number) => {
        return (
          <SelectOption
            key={index}
            value={item.value}
            label={item.label}
            prefix={props?.icon}
          />
        );
      })}
    </Select>
  );
};
